import {
  CourseResponse,
  CourseId,
  CourseStepResponse,
  CourseStepId,
  CourseSectionId,
  CourseSectionResponse,
  CourseSearchParams,
  CourseType,
  CourseStatus,
} from '@business/entities/course';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';

import { R } from '@core/utils/r';
import { CourseAPI } from '@business/api/course';
import { of } from 'rxjs';
import { UserId } from '@business/entities/account';

export class CourseBloc extends BaseBloc {
  private readonly courseRepo = new Repository<CourseResponse>({
    getItemId: course => course.id,
  });

  readonly courses$ = this.courseRepo.items$;
  readonly course$ = this.courseRepo.selectItem;
  readonly step$ = (courseId: CourseId, sectionId: CourseSectionId, stepId: CourseStepId) => {
    return this.courseRepo.selectItem(courseId).pipe(
      map(course => {
        if (course) {
          const sections = (R.get(course, 'sections') || []) as CourseSectionResponse[];
          const section = sections.find(s => s.id === sectionId);
          if (section) {
            return section.steps.find(s => s.id === stepId);
          }
        }
        return null;
      }),
    );
  };

  onReset() {
    this.courseRepo.reset();
  }

  fetchCourses = (data?: CourseSearchParams) => {
    return CourseAPI.fetchCourses(data).pipe(this.courseRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_courses_error') }));
  };

  fetchCourse = (id: CourseId) => {
    return CourseAPI.fetchCourse(id).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_courses_error') }),
    );
  };

  createCourse = (checklist: Partial<CourseResponse>, userId?: UserId) => {
    return CourseAPI.createCourse(checklist, userId).pipe(
      this.courseRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_course'),
      }),
    );
  };

  duplicateCourse = (id: CourseId, checklist: Partial<CourseResponse>) => {
    return CourseAPI.duplicateCourse(id, checklist).pipe(
      this.courseRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('duplicate_course'),
      }),
    );
  };

  updateCourse = (id: CourseId, data: Partial<CourseResponse>) => {
    return CourseAPI.updateCourse(id, data).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_course'),
      }),
    );
  };

  updateCourseArchive = (id: CourseId, data: Partial<CourseResponse>) => {
    return CourseAPI.updateCourseArchive(id, data).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage(data.type == CourseType.active ? 'unarchive_course' : 'archive_course'),
      }),
    );
  };

  deleteCourse = (id: CourseId) => {
    return CourseAPI.deleteCourse(id).pipe(
      this.courseRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_course'),
      }),
    );
  };

  /* Sections */
  createCourseSection = (courseId: CourseId, name: string) => {
    return CourseAPI.createCourseSection(courseId, name).pipe(
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  updateCourseSection = (courseId: CourseId, id: number, name: string) => {
    return CourseAPI.updateCourseSection(id, name).pipe(
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('update_course_section'),
      }),
    );
  };

  deleteCourseSection = (courseId: CourseId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this section?').pipe(
      switchMap(() => CourseAPI.deleteCourseSection(id)),
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('delete_course_section'),
      }),
    );
  };

  sortSections = (courseId: CourseId, sectionIds: number[]) => {
    return CourseAPI.sortSections(courseId, sectionIds).pipe(
      tap(() => {
        const checklist = this.courseRepo.getItem(courseId)! as CourseResponse;
        const newSections = checklist.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.courseRepo.updateOne({
          id: courseId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  /* Steps */

  createCourseStep = (courseId: CourseId, sectionId: number, data: Partial<CourseStepResponse>) => {
    return CourseAPI.createCourseStep(sectionId, data).pipe(
      switchMap(step => this.fetchCourse(courseId).pipe(map(() => step))),
      handleMessage({
        error: requestMessage('create_course_section_step'),
      }),
    );
  };

  updateCourseStep = (courseId: CourseId, id: number, data: Partial<CourseStepResponse>) => {
    return CourseAPI.updateCourseStep(id, data).pipe(
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('update_course_section_step'),
      }),
    );
  };

  deleteCourseStep = (courseId: CourseId, id: number) => {
    return showDeleteConfirmation('Delete Lesson', 'Do you really want to remove this lesson?').pipe(
      switchMap(() => CourseAPI.deleteCourseStep(id)),
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('delete_course_section_step'),
      }),
    );
  };

  stepCopy = (courseId: CourseId, id: number) => {
    return CourseAPI.stepCopy(courseId, id).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section_step'),
      }),
    );
  };

  sortSectionSteps = (courseId: CourseId, sectionId: number, stepIds: number[]) => {
    return CourseAPI.sortSectionSteps(courseId, stepIds).pipe(
      tap(() => {
        const checklist = this.courseRepo.getItem(courseId)! as CourseResponse;
        const affectedSection = R.find(checklist.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = checklist.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.courseRepo.updateOne({
          id: courseId,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_steps_sort'),
      }),
    );
  };

  updateStepName = (courseId: CourseId, id: number, name: string) => {
    return CourseAPI.updateCourseStepName(id, name).pipe(
      switchMap(() => this.fetchCourse(courseId)),
      handleMessage({
        error: requestMessage('update_course_section_step'),
      }),
    );
  };

  enroll = (courseId: CourseId, uids: UserId[]) => {
    return CourseAPI.enroll(courseId, uids).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  restart = (courseId: CourseId, uid: UserId) => {
    return CourseAPI.restart(courseId, uid).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  bulkRestart = (courseId: CourseId, uids: UserId[]) => {
    return CourseAPI.bulkRestart(courseId, uids).pipe(
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  unenroll = (courseId: CourseId, uid: UserId) => {
    return showDeleteConfirmation('Unenroll User', 'Do you really want to unenroll this user from this course?', { btnLabel: 'Unenroll' }).pipe(
      switchMap(() => CourseAPI.unenroll(courseId, uid)),

      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  updateFormData = (id: string, data: string) => {
    return CourseAPI.updateFormData(id, data).pipe(
      handleMessage({
        error: requestMessage('fetch_proposal_template_error'),
      }),
    );
  };

  getStep = (stepId: string) => {
    return CourseAPI.getStep(stepId).pipe(
      handleMessage({
        error: requestMessage('fetch_proposal_template_error'),
      }),
    );
  };

  updateCourseStatus = (id: CourseId, data: Partial<CourseResponse>) => {
    return CourseAPI.updateCourseStatus(id, data).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage(data.status == CourseStatus.publish ? 'publish_course' : 'unpublish_course'),
      }),
    );
  };

  sortUserCourses = (userId: UserId, sortedCourseIds: CourseId[]) => {
    return CourseAPI.sortUserCourses(userId, sortedCourseIds).pipe(
      handleMessage({
        error: requestMessage('sort_course_error'),
      }),
    );
  };

  generateCertificatePdf = (id: CourseId) => {
    return CourseAPI.generateCertificatePdf(id).pipe(handleMessage({ error: requestMessage('fetch_graph_pdf_error') }));
  };
}
